/**
 * 一元配置 ANOVA の計算過程 walkthrough（レガシー ANOVAView calculationDetails 相当）
 */

import type { ANOVAResult } from './statistics/anova';
import type { DataRow } from '../stores/systems/keisuke/dashboardStore';
import { parseFiniteNumberFromCell } from './keisukeGridData';

export type AnovaWalkthroughTranslate = (
  key: string,
  options?: Record<string, number | string>
) => string;

export type OneWayAnovaMethodKind = 'standard' | 'repeated' | 'lmm';

function formatNumber(value: number, digits = 4): string {
  if (!Number.isFinite(value)) return 'NaN';
  const fixed = value.toFixed(digits);
  return fixed.replace(/\.?0+$/, '');
}

function formatPValue(value: number): string {
  if (!Number.isFinite(value)) return 'NaN';
  if (value <= 0) return '< 1×10⁻¹⁵';
  if (value < 0.0001) return value.toExponential(2);
  return value.toFixed(4);
}

export function buildGroupValueMapFromGrid(
  gridData: DataRow[],
  groupCol: string,
  valueCol: string
): Record<string, number[]> {
  const map: Record<string, number[]> = {};
  for (const row of gridData) {
    const group = String(row[groupCol] ?? '').trim();
    const num = parseFiniteNumberFromCell(row[valueCol]);
    if (!group || num === null) continue;
    if (!map[group]) map[group] = [];
    map[group].push(num);
  }
  return map;
}

function postHocLabelForWalkthrough(postHoc: string | undefined): string {
  if (postHoc === 'bonferroni') return 'Bonferroni';
  if (postHoc === 'holm') return 'Holm';
  if (postHoc === 'scheffe') return 'Scheffe';
  if (postHoc === 'dunnett') return 'Dunnett';
  if (postHoc === 'tukey') return 'Tukey HSD';
  return postHoc ?? '';
}

export function buildOneWayAnovaWalkthrough(params: {
  result: ANOVAResult;
  groupValueMap: Record<string, number[]>;
  t: AnovaWalkthroughTranslate;
  oneWayMethod?: OneWayAnovaMethodKind;
  postHoc?: string;
  controlGroupName?: string;
  effectiveAlpha?: number;
}): string {
  const {
    result,
    groupValueMap,
    t,
    oneWayMethod = 'standard',
    postHoc = '',
    controlGroupName = '',
    effectiveAlpha = 0.05,
  } = params;

  const calcText = (key: string, options?: Record<string, number | string>) =>
    t(`keisuke.anova.calculation.${key}`, options);

  const formatValueList = (values: number[]) =>
    values.map((value) => formatNumber(value, 4)).join(', ');
  const groupOrder = result.groups.map((group) => group.name);
  const totalN = groupOrder.reduce((sum, name) => sum + (groupValueMap[name]?.length ?? 0), 0);
  const totalSum = groupOrder.reduce(
    (sum, name) => sum + (groupValueMap[name]?.reduce((a, v) => a + v, 0) ?? 0),
    0
  );
  const overallMean = totalN > 0 ? totalSum / totalN : 0;
  const postHocLabel = postHocLabelForWalkthrough(postHoc);
  const lines: string[] = [];

  if (oneWayMethod === 'lmm') {
    lines.push(calcText('oneWay.section1Title'));
    lines.push('');
    groupOrder.forEach((name) => {
      const values = groupValueMap[name] ?? [];
      lines.push(
        calcText('common.groupValues', {
          name,
          count: values.length,
          values: formatValueList(values),
        })
      );
    });
    lines.push(
      calcText('common.totalCount', { totalN, groupCount: groupOrder.length })
    );
    lines.push('');
    lines.push(calcText('oneWay.lmm.section2Title'));
    lines.push(calcText('oneWay.lmm.meaning'));
    lines.push(
      calcText('oneWay.lmm.waldTest', {
        dfBetween: result.dfBetween,
        dfWithin: result.dfWithin,
        fValue: formatNumber(result.f, 4),
      })
    );
    lines.push(calcText('common.pValue', { pValue: formatPValue(result.pValue) }));
    if (result.varianceComponents) {
      lines.push(
        calcText('oneWay.lmm.randomInterceptVariance', {
          value: formatNumber(result.varianceComponents.randomIntercept, 6),
        })
      );
      lines.push(
        calcText('common.residualVariance', {
          value: formatNumber(result.varianceComponents.residual, 6),
        })
      );
    }
    lines.push(calcText('oneWay.lmm.note'));
    return lines.join('\n');
  }

  if (oneWayMethod === 'repeated') {
    lines.push(calcText('oneWay.section1Title'));
    lines.push('');
    groupOrder.forEach((name) => {
      const values = groupValueMap[name] ?? [];
      lines.push(
        calcText('common.groupValues', {
          name,
          count: values.length,
          values: formatValueList(values),
        })
      );
    });
    lines.push(
      calcText('common.totalCount', { totalN, groupCount: groupOrder.length })
    );
    lines.push('');
    lines.push(calcText('oneWay.repeated.section2Title'));
    lines.push(`SS_between = ${formatNumber(result.ssBetween, 6)}, df_between = ${result.dfBetween}`);
    lines.push(calcText('oneWay.repeated.ssBetweenMeaning'));
    if (result.ssSubject !== undefined && result.dfSubject !== undefined) {
      lines.push(`SS_subject = ${formatNumber(result.ssSubject, 6)}, df_subject = ${result.dfSubject}`);
      lines.push(calcText('oneWay.repeated.ssSubjectMeaning'));
    }
    lines.push(`SS_error = ${formatNumber(result.ssWithin, 6)}, df_error = ${result.dfWithin}`);
    lines.push(calcText('oneWay.repeated.ssErrorMeaning'));
    lines.push('');
    lines.push(calcText('oneWay.repeated.section3Title'));
    lines.push(`MS_between = ${formatNumber(result.msBetween, 6)}`);
    lines.push(calcText('oneWay.repeated.msBetweenMeaning'));
    if (result.msSubject !== undefined) {
      lines.push(`MS_subject = ${formatNumber(result.msSubject, 6)}`);
      lines.push(calcText('oneWay.repeated.msSubjectMeaning'));
    }
    lines.push(`MS_error = ${formatNumber(result.msWithin, 6)}`);
    lines.push(calcText('oneWay.repeated.msErrorMeaning'));
    lines.push(`F = MS_between / MS_error = ${formatNumber(result.f, 6)}`);
    lines.push('');
    lines.push(calcText('oneWay.repeated.section4Title'));
    lines.push(`P = ${formatPValue(result.pValue)}`);
    return lines.join('\n');
  }

  lines.push(calcText('oneWay.section1Title'));
  lines.push('');
  groupOrder.forEach((name) => {
    const values = groupValueMap[name] ?? [];
    lines.push(
      calcText('common.groupValues', {
        name,
        count: values.length,
        values: formatValueList(values),
      })
    );
  });
  lines.push(calcText('common.totalCount', { totalN, groupCount: groupOrder.length }));
  lines.push('');
  lines.push(calcText('oneWay.standard.groupMeansTitle'));
  groupOrder.forEach((name) => {
    const values = groupValueMap[name] ?? [];
    const sum = values.reduce((acc, value) => acc + value, 0);
    const mean = values.length > 0 ? sum / values.length : 0;
    const sumText = values.map((value) => formatNumber(value, 4)).join(' + ');
    lines.push(
      `x_bar_${name} = (${sumText}) / ${values.length} = ${formatNumber(sum, 4)} / ${values.length} = ${formatNumber(mean, 4)}`
    );
  });
  lines.push('');
  lines.push(calcText('oneWay.standard.overallMeanTitle'));
  lines.push(calcText('oneWay.standard.totalSum', { totalSum: formatNumber(totalSum, 4) }));
  lines.push(`x_bar = ${formatNumber(totalSum, 4)} / ${totalN} = ${formatNumber(overallMean, 6)}`);
  lines.push('');
  lines.push(calcText('oneWay.standard.section2Title'));
  lines.push(calcText('oneWay.standard.ssbIntro'));
  lines.push(calcText('oneWay.standard.ssbFormula'));
  lines.push(
    calcText('oneWay.standard.ssbAssumption', {
      groupSize: groupOrder.length > 0 ? (groupValueMap[groupOrder[0]]?.length ?? 0) : 0,
    })
  );
  lines.push('');
  groupOrder.forEach((name) => {
    const values = groupValueMap[name] ?? [];
    const sum = values.reduce((acc, value) => acc + value, 0);
    const mean = values.length > 0 ? sum / values.length : 0;
    const diff = mean - overallMean;
    const diffSq = diff * diff;
    const contribution = values.length * diffSq;
    lines.push(
      `${name}: ${values.length} * (${formatNumber(mean, 6)} - ${formatNumber(overallMean, 6)})^2 = ${formatNumber(contribution, 6)}`
    );
  });
  lines.push('');
  lines.push(calcText('oneWay.standard.ssbTotal', { value: formatNumber(result.ssBetween, 6) }));
  lines.push('');
  lines.push(calcText('oneWay.standard.section3Title'));
  lines.push(calcText('oneWay.standard.sswFormula'));
  groupOrder.forEach((name) => {
    const values = groupValueMap[name] ?? [];
    const sum = values.reduce((acc, value) => acc + value, 0);
    const mean = values.length > 0 ? sum / values.length : 0;
    lines.push(calcText('common.groupMeanLine', { name, mean: formatNumber(mean, 4) }));
    let groupSum = 0;
    values.forEach((value) => {
      const sq = (value - mean) ** 2;
      groupSum += sq;
      lines.push(`(${formatNumber(value, 4)} - ${formatNumber(mean, 4)})^2 = ${formatNumber(sq, 4)}`);
    });
    lines.push(calcText('common.sumLine', { value: formatNumber(groupSum, 4) }));
    lines.push('');
  });
  lines.push(`SSW = ${formatNumber(result.ssWithin, 4)}`);
  lines.push('');
  lines.push(calcText('oneWay.standard.section4Title'));
  lines.push(`df_between = k - 1 = ${groupOrder.length} - 1 = ${result.dfBetween}`);
  lines.push(`df_within = N - k = ${totalN} - ${groupOrder.length} = ${result.dfWithin}`);
  lines.push(
    `MSB = SSB / df_between = ${formatNumber(result.ssBetween, 6)} / ${result.dfBetween} = ${formatNumber(result.msBetween, 6)}`
  );
  lines.push(
    `MSW = SSW / df_within = ${formatNumber(result.ssWithin, 6)} / ${result.dfWithin} = ${formatNumber(result.msWithin, 6)}`
  );
  lines.push(
    `F = MSB / MSW = ${formatNumber(result.msBetween, 6)} / ${formatNumber(result.msWithin, 6)} = ${formatNumber(result.f, 6)}`
  );
  lines.push('');
  lines.push(calcText('oneWay.standard.section5Title'));
  lines.push(`p = P(F_${result.dfBetween},${result.dfWithin} >= ${formatNumber(result.f, 4)})`);
  lines.push(`p ≈ ${formatPValue(result.pValue)}`);
  lines.push('');
  lines.push(calcText('oneWay.standard.section6Title', { postHocLabel }));
  if (postHoc === 'tukey') {
    const ns = result.groups.map((group) => group.n);
    const avgN = ns.length > 0 ? ns.reduce((acc, value) => acc + value, 0) / ns.length : 0;
    const mswOverN = avgN > 0 ? result.msWithin / avgN : 0;
    lines.push(calcText('oneWay.standard.postHocThreshold', { postHocLabel }));
    lines.push('HSD = q_alpha(k, df_within) * sqrt(MSW / n)');
    lines.push(
      `k=${groupOrder.length}, df_within=${result.dfWithin}, n≈${formatNumber(avgN, 4)}, MSW=${formatNumber(result.msWithin, 6)}`
    );
    lines.push(`MSW / n = ${formatNumber(mswOverN, 6)}`);
    if (groupOrder.length === 3 && Math.abs(effectiveAlpha - 0.05) < 1e-9) {
      if (result.dfWithin === 12 || result.dfWithin === 8) {
        lines.push(calcText('oneWay.standard.tukeyNote'));
      } else {
        lines.push(calcText('oneWay.standard.tukeyNoteNoTable'));
      }
    } else {
      lines.push(calcText('oneWay.standard.tukeyNoteNoTable'));
    }
  } else if (postHoc === 'bonferroni' || postHoc === 'holm') {
    lines.push(calcText('oneWay.standard.postHocSelected', { postHocLabel }));
    lines.push('t = |x_bar_i - x_bar_j| / sqrt(MSW × (1/n_i + 1/n_j))');
    lines.push(
      postHoc === 'bonferroni'
        ? calcText('oneWay.standard.correctionBonferroni')
        : calcText('oneWay.standard.correctionHolm')
    );
  } else if (postHoc === 'scheffe') {
    lines.push(calcText('oneWay.standard.postHocSelected', { postHocLabel }));
    lines.push('F = t^2 / (k - 1)');
    lines.push(calcText('oneWay.standard.scheffeNote'));
  } else if (postHoc === 'dunnett') {
    lines.push(calcText('oneWay.standard.postHocSelected', { postHocLabel }));
    lines.push(calcText('oneWay.standard.dunnettNote'));
  } else if (postHoc) {
    lines.push(calcText('oneWay.standard.postHocSelected', { postHocLabel }));
    lines.push(calcText('oneWay.standard.omittedDetail'));
  }

  const pairLines: string[] = [];
  const controlName = controlGroupName || groupOrder[0];
  if (postHoc === 'dunnett') {
    groupOrder.forEach((name) => {
      if (name === controlName) return;
      const values = groupValueMap[name] ?? [];
      const controlValues = groupValueMap[controlName] ?? [];
      const mean = values.length > 0 ? values.reduce((s, v) => s + v, 0) / values.length : 0;
      const controlMean =
        controlValues.length > 0 ? controlValues.reduce((s, v) => s + v, 0) / controlValues.length : 0;
      pairLines.push(
        `${name} - ${controlName} = ${formatNumber(mean, 4)} - ${formatNumber(controlMean, 4)} = ${formatNumber(mean - controlMean, 4)}`
      );
    });
  } else {
    groupOrder.forEach((name, index) => {
      const values = groupValueMap[name] ?? [];
      const mean = values.length > 0 ? values.reduce((s, v) => s + v, 0) / values.length : 0;
      for (let j = index + 1; j < groupOrder.length; j += 1) {
        const otherName = groupOrder[j];
        const otherValues = groupValueMap[otherName] ?? [];
        const otherMean =
          otherValues.length > 0 ? otherValues.reduce((s, v) => s + v, 0) / otherValues.length : 0;
        pairLines.push(
          `${otherName} - ${name} = ${formatNumber(otherMean, 4)} - ${formatNumber(mean, 4)} = ${formatNumber(otherMean - mean, 4)}`
        );
      }
    });
  }
  if (pairLines.length > 0) {
    lines.push('');
    lines.push(calcText('common.diffTitle'));
    pairLines.forEach((line) => lines.push(line));
  }

  return lines.join('\n');
}
